/**
 * Unified Electron API module
 */

export { ElectronAPI } from './base/ElectronAPI';
export { ExclusionAPI as AppExclusionAPI } from './features/AppExclusionAPI';
export { CategoryExclusionAPI } from './features/CategoryExclusionAPI';
export { AuthAPI } from './features/AuthAPI';
export { ContextAPI } from './features/ContextAPI';
export { MenuAPI } from './features/MenuAPI';
export { SystemAPI } from './features/SystemAPI';
export {
  TranscriptionInputDeviceError,
  TranscriptionInputDeviceErrorCode,
} from './features/TranscriptionInputDeviceAPI';
export { PlatformActionError } from './features/PlatformActionsAPI';

export type {
  ElectronEventCallback,
  ElectronListenerMethod,
  ElectronListenerOptions,
  ElectronUnsubscribe,
  IPCEventType,
  ContextKitResponse,
} from './base/types';

export type {
  AppWithExclusionState,
  DomainWithExclusionState,
} from './features/AppExclusionAPI';
export type { CategoryID, CategoryInfo } from '../../../electron/shared/types';
export {
  CATEGORY_IDS,
  DEFAULT_ENABLED_CATEGORY_IDS,
} from '../../../electron/shared/types';

import { ExclusionAPI } from './features/AppExclusionAPI';
import { AuthAPI } from './features/AuthAPI';
import { AxonMcpAPI } from './features/AxonMcpAPI';
import { CanvasExportAPI } from './features/CanvasExportAPI';
import { CategoryExclusionAPI } from './features/CategoryExclusionAPI';
import { ChromeImportAPI } from './features/ChromeImportAPI';
import { CodingAgentsAPI } from './features/CodingAgentsAPI';
import { ContextAPI } from './features/ContextAPI';
import { DictationAPI } from './features/DictationAPI';
import { EventKitAPI } from './features/EventKitAPI';
import { FeatureFlagsAPI } from './features/FeatureFlagsAPI';
import { HummingbirdAPI } from './features/HummingbirdAPI';
import { LbNotificationsAPI } from './features/LbNotificationsAPI';
import { LocalTerminalAPI } from './features/LocalTerminalAPI';
import { MeetingDebugAPI } from './features/MeetingDebugAPI';
import { MeetingIntentOverlayAPI } from './features/MeetingIntentOverlayAPI';
import { MeetingNotesAPI } from './features/MeetingNotesAPI';
import { MenuAPI } from './features/MenuAPI';
import { PlatformActionsAPI } from './features/PlatformActionsAPI';
import { SnapshotDumpAPI } from './features/SnapshotDumpAPI';
import { SyncTelemetryAPI } from './features/SyncTelemetryAPI';
import { SystemAPI } from './features/SystemAPI';
import { TranscriptionInputDeviceAPI } from './features/TranscriptionInputDeviceAPI';
import { WebSocketAPI } from './features/WebSocketAPI';
import { WindowAPI } from './features/WindowAPI';

export const electronApis = {
  codingAgents: new CodingAgentsAPI(),
  appExclusion: new ExclusionAPI(),
  axonMcp: new AxonMcpAPI(),
  canvasExport: new CanvasExportAPI(),
  chromeImport: new ChromeImportAPI(),
  categoryExclusion: new CategoryExclusionAPI(),
  auth: new AuthAPI(),
  context: new ContextAPI(),
  eventKit: new EventKitAPI(),
  system: new SystemAPI(),
  window: new WindowAPI(),
  meetings: new MeetingNotesAPI(),
  meetingDebug: new MeetingDebugAPI(),
  meetingIntentOverlay: new MeetingIntentOverlayAPI(),
  menu: new MenuAPI(),
  featureFlags: new FeatureFlagsAPI(),
  lbNotifications: new LbNotificationsAPI(),
  localTerminal: new LocalTerminalAPI(),
  hummingbird: new HummingbirdAPI(),
  dictation: new DictationAPI(),
  webSocket: new WebSocketAPI(),
  transcriptionInputDevice: new TranscriptionInputDeviceAPI(),
  platformActions: new PlatformActionsAPI(),
  snapshotDump: new SnapshotDumpAPI(),
  syncTelemetry: new SyncTelemetryAPI(),
};

/**
 * Clear all Electron listeners across all API instances
 * Similar to clearAllNativeListeners
 */
export const clearAllElectronListeners = () => {
  electronApis.appExclusion.clearAllListeners();
  electronApis.categoryExclusion.clearAllListeners();
  electronApis.auth.clearAllListeners();
  electronApis.context.clearAllListeners();
  electronApis.system.clearAllListeners();
  electronApis.meetings.clearAllListeners();
  electronApis.meetingIntentOverlay.clearAllListeners();
  electronApis.featureFlags.clearAllListeners();
  electronApis.lbNotifications.clearAllListeners();
  electronApis.hummingbird.clearAllListeners();
  electronApis.snapshotDump.clearAllListeners();
};

// Backward compatibility - export the old structure
export const electronApi = electronApis.system;
